import React, { useState, useEffect } from 'react';
import { Trophy, Flame, Target, TrendingUp } from 'lucide-react';
import { progressAPI } from '../services/api';
import { triggerMilestoneAchieved } from '../lib/confetti';

const ProgressMilestones = () => {
  const [milestones, setMilestones] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastMilestone, setLastMilestone] = useState(null);

  useEffect(() => {
    loadMilestones();
  }, []);

  useEffect(() => {
    // Trigger celebration when a new milestone is reached
    if (milestones?.milestone && milestones.milestone.level !== lastMilestone) {
      if (lastMilestone !== null) {
        triggerMilestoneAchieved(milestones.milestone.level);
      }
      setLastMilestone(milestones.milestone.level);
    }
  }, [milestones?.milestone?.level]);

  const loadMilestones = async () => {
    try {
      setLoading(true);
      const data = await progressAPI.getMilestones();
      setMilestones(data);
    } catch (error) {
      console.error('Error loading milestones:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="gradient-card rounded-2xl p-6 border border-white/20 dark:border-white/10">
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/3"></div>
          <div className="h-8 bg-slate-200 dark:bg-slate-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (!milestones || milestones.totalHabits === 0) {
    return null;
  }

  const { percentage, milestone, currentProgress, monthlyGoal, streakInfo, completionsToday, totalHabits } = milestones;

  // Determine progress bar color based on percentage
  const getProgressColor = () => {
    if (percentage >= 100) return 'from-yellow-400 to-amber-500';
    if (percentage >= 75) return 'from-green-400 to-emerald-500';
    if (percentage >= 50) return 'from-blue-400 to-cyan-500';
    if (percentage >= 25) return 'from-purple-400 to-pink-500';
    return 'from-slate-400 to-slate-500';
  };

  return (
    <div 
      className="gradient-card rounded-2xl p-6 border border-white/20 dark:border-white/10"
      data-testid="progress-milestones"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
          <Target className="w-5 h-5 text-blue-500" />
          Monthly Progress
        </h3>
        {milestone && (
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            milestone.level === 'perfect' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
            milestone.level === 'gold' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
            milestone.level === 'silver' ? 'bg-slate-100 text-slate-700 dark:bg-slate-700/30 dark:text-slate-300' :
            'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
          }`}>
            {milestone.emoji} {milestone.label}
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-slate-600 dark:text-slate-400">
            {currentProgress} / {monthlyGoal} completions
          </span>
          <span className="font-semibold text-slate-800 dark:text-slate-200">
            {percentage}%
          </span>
        </div>
        <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
          <div 
            className={`h-full bg-gradient-to-r ${getProgressColor()} transition-all duration-500 ease-out rounded-full`}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
        
        {/* Milestone markers */}
        <div className="relative h-2 mt-1">
          {[25, 50, 75, 100].map((mark) => (
            <div 
              key={mark}
              className={`absolute top-0 w-1 h-2 rounded-full transition-colors ${
                percentage >= mark ? 'bg-green-500' : 'bg-slate-300 dark:bg-slate-600'
              }`}
              style={{ left: `${mark}%`, transform: 'translateX(-50%)' }}
            />
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-4 mt-4">
        <div className="text-center p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
          <div className="flex items-center justify-center gap-1 text-orange-500 mb-1">
            <Flame className="w-4 h-4" />
            <span className="text-lg font-bold">{streakInfo.currentStreak}</span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Current Streak</p>
        </div>
        
        <div className="text-center p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
          <div className="flex items-center justify-center gap-1 text-purple-500 mb-1">
            <Trophy className="w-4 h-4" />
            <span className="text-lg font-bold">{streakInfo.longestStreak}</span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Best Streak</p>
        </div>
        
        <div className="text-center p-3 rounded-xl bg-slate-50 dark:bg-slate-800/50">
          <div className="flex items-center justify-center gap-1 text-green-500 mb-1">
            <TrendingUp className="w-4 h-4" />
            <span className="text-lg font-bold">{completionsToday}/{totalHabits}</span>
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">Today</p>
        </div>
      </div>
    </div>
  );
};

export default ProgressMilestones;
